import { defineComponent as _defineComponent } from 'vue'
import BSelect from 'JS/components/blain-form/BSelect.vue';
import bugsnagClient from 'JS/global/bugsnag';
import DatePickerDropdown from 'JS/components/date-picker/DatePickerDropdown.vue';
import Icon from 'JS/components/Icon.vue';
import LinkButton from 'JS/components/LinkButton.vue';
import Price from 'JS/components/price/Price.vue';
import QuantityField from 'JS/components/QuantityField.vue';
import SideMenu from 'JS/components/SideMenu.vue';
import SideMenuHeaderButton from 'JS/components/side-menus/SideMenuHeaderButton.vue';
import Spinner from 'JS/components/Spinner.vue';
import SubscriptionService from 'JS/services/SubscriptionService';
import UiButton from 'JS/components/UiButton.vue';
import { addDays, eachDayOfInterval } from "date-fns/fp";
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue';
import { createDateLookup } from "JS/automotive/auto-service-scheduler/utils/calendar";
import { getFrequencySelectionLabel as getFrequencyOptionLabel } from 'JS/subscriptions/shared/utilities/frequency';
import { isSameDay } from "date-fns/fp";
import { ProductSubscriptionBenefits } from 'JS/types/Order';
import { SubscriptionOverview } from 'JS/types/subscription';


export default /*#__PURE__*/_defineComponent({
  __name: 'ManageSubscriptionSideMenu',
  props: {
    show: { type: Boolean },
    subscription: null
  },
  emits: ["close", "changed-values"],
  setup(__props: any, { emit }: { emit: ({
	(event: 'close', update?: boolean): void;
	(event: 'changed-values', values: string[]): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	show: boolean,
	subscription: SubscriptionOverview;
}

//Imports




const _subscriptionService = new SubscriptionService();
const showNestedPanel = ref<'cancel'|'error'|null>(null);
const editedSubscription = ref<SubscriptionOverview>();
const selectedReminderDate = ref<Date>(new Date(props.subscription.nextReminderDate));
const isButtonDisabled = ref<boolean>(false);
const loading = ref<boolean>(false);
const errorFocuser = ref<HTMLElement | null>();
const cancelFocuser = ref<HTMLElement | null>();
const cancelButtonFocuser = ref<Vue | null>();

const isActiveReward = computed(()=> {
	return editedSubscription.value.quantity >= 20 ? 'active-colors' : '';
});

const customAvailableDates = computed(()=> {
	const today = new Date();
	const nextReminderDate = new Date(props.subscription.nextReminderDate);

	if (addDays(1, today) > addDays(168, new Date(props.subscription.lastReminderDate))) {
		return createDateLookup([nextReminderDate]);
	}

	const dateArray = eachDayOfInterval({ start: addDays(1, today), end: addDays(168, new Date(props.subscription.lastReminderDate)) });
	//First two checks not necessary but will shorten runtime.
	if (today > nextReminderDate || isSameDay(today, nextReminderDate) || !dateArray.find(d => isSameDay(d, nextReminderDate))) {
		dateArray.unshift(nextReminderDate);
	}
	return createDateLookup(dateArray);
});

function handleCalendarToggle(isOpening: boolean) {
	isButtonDisabled.value = isOpening;
}

function handleCancelClick() {
	showNestedPanel.value = 'cancel';
	nextTick(()=> {
		cancelFocuser.value?.focus();
	});
}

function handleClosePanel() {
	if (showNestedPanel.value === 'cancel') {
		showNestedPanel.value = null;
		nextTick(()=> {
			(cancelButtonFocuser.value?.$el as HTMLElement).focus();
		});
	} else {
		showNestedPanel.value = null;
	}
}

function handleUpdateDate(newDate: Date) {
	selectedReminderDate.value = newDate;
	isButtonDisabled.value = false;
}

async function submitChanges() {
	loading.value = true;
	const changedValues = [];
	if (editedSubscription.value.quantity !== props.subscription.quantity) {
		changedValues.push('quantity');
	}
	if (editedSubscription.value.frequency !== props.subscription.frequency) {
		changedValues.push('frequency');
	}
	if (!isSameDay(selectedReminderDate.value, new Date(props.subscription.nextReminderDate))) {
		changedValues.push('order confirmation date');
	}

	if (changedValues.length < 1) {
		emit('close', false);
		loading.value = false;
	} else {
		let success = false;
		try {
			success = await _subscriptionService.updateSubscription({
				subscriptionID: editedSubscription.value.id,
				frequency: editedSubscription.value.frequency,
				quantity: editedSubscription.value.quantity,
				nextReminderDate: selectedReminderDate.value.toDateString(),
			});

			if (success) {
				emit('changed-values', changedValues);
			} else {
				showNestedPanel.value = 'error';
				errorFocuser.value?.focus();
			}
		} catch (exception) {
			showNestedPanel.value = 'error';
			errorFocuser.value?.focus();
			bugsnagClient.notify(exception);
		} finally {
			loading.value = false;
		}
	}
}

async function confirmCancelSubscription() {
	loading.value = true;
	let success = false;

	try {
		success = await _subscriptionService.cancelSubscription(props.subscription.id);
	} catch (exception) {
		bugsnagClient.notify(exception);
	} finally {
		if (success) {
			emit('close', true);
		} else {
			showNestedPanel.value = 'error';
			errorFocuser.value?.focus();
		}
		loading.value = false;
	}
}

function farmsBenefitLineItem(benefit: ProductSubscriptionBenefits): string {
	return benefit.lineItem.replace(benefit.boldText, `<strong>${benefit.boldText}</strong>`);
}

function handleHidden() {
	selectedReminderDate.value = new Date(props.subscription.nextReminderDate);
}

watch(()=> props.show, (isShowing) => {
	if (isShowing) {
		editedSubscription.value = { ...props.subscription };
		selectedReminderDate.value = new Date(props.subscription.nextReminderDate);
	}
});

onBeforeMount(() => {
	editedSubscription.value = { ...props.subscription };
});


return { __sfc: true,props, emit, _subscriptionService, showNestedPanel, editedSubscription, selectedReminderDate, isButtonDisabled, loading, errorFocuser, cancelFocuser, cancelButtonFocuser, isActiveReward, customAvailableDates, handleCalendarToggle, handleCancelClick, handleClosePanel, handleUpdateDate, submitChanges, confirmCancelSubscription, farmsBenefitLineItem, handleHidden, BSelect, DatePickerDropdown, Icon, LinkButton, Price, QuantityField, SideMenu, SideMenuHeaderButton, Spinner, UiButton, getFrequencyOptionLabel }
}

})